/**
 * Integration seams for the YIP Studio.
 *
 * The studio touches the YIP ecosystem at exactly four boundaries. Each is an
 * interface (a "seam") with a STANDALONE adapter so the studio runs fully on its
 * own today. When we go live, we add INTEGRATED adapters (Clerk, the platform AI
 * API, S3, dApp publish) and flip them in the registry at the bottom — no feature
 * code changes. That single switch is the whole point of this file.
 */

import {
  Game,
  GenerationRequest,
  GenerationResult,
  SceneObject,
} from './types/game';

// ============================================================
// Shared types
// ============================================================

export type StakeholderClass = 'casual' | 'creator' | 'professional';

export interface CreatorIdentity {
  clerkUserId: string;        // stable identity → becomes Game.ownerId
  displayName?: string;
  walletAddress?: string;     // Solana, for on-chain bindings (optional now)
  stakeholderClass?: StakeholderClass; // role → gates which surfaces a user sees
  tier?: string;              // paid/usage tier → gates how much they can do
}

export interface StoredAsset {
  id: string;
  url: string;
  kind: string;
  name: string;
  contentType?: string;
}

export interface PublishResult {
  status: 'published' | 'exported';
  url?: string;
  details?: string;
}

// ============================================================
// Seam 1 — Identity (who the creator is)
// ============================================================

export interface IdentityProvider {
  getCurrentIdentity(): Promise<CreatorIdentity | null>;
  isAuthenticated(): Promise<boolean>;
}

// ============================================================
// Seam 2 — Generation (request an AI capability)
// ============================================================

export interface GenerationGateway {
  generate(
    request: GenerationRequest,
    identity: CreatorIdentity | null
  ): Promise<GenerationResult>;
}

// ============================================================
// Seam 3 — Asset storage (persist/fetch binaries)
// ============================================================

export interface AssetStore {
  put(input: {
    name: string;
    kind: string;
    data: string;            // base64
    contentType?: string;
  }): Promise<StoredAsset>;
  get(id: string): Promise<StoredAsset | null>;
  list(): Promise<StoredAsset[]>;
}

// ============================================================
// Seam 4 — Publish (push a finished game out)
// ============================================================

export interface PublishTarget {
  publish(game: Game, identity: CreatorIdentity | null): Promise<PublishResult>;
}

// ============================================================
// Standalone adapters (work with zero ecosystem dependencies)
// ============================================================

export class StandaloneIdentityProvider implements IdentityProvider {
  async getCurrentIdentity(): Promise<CreatorIdentity> {
    return {
      clerkUserId: 'dev-user',
      displayName: 'Dev Creator',
      stakeholderClass: 'creator',
      tier: 'dev',
    };
  }
  async isAuthenticated(): Promise<boolean> {
    return true;
  }
}

const STUB_WARNING =
  'Standalone stub — connect the platform AI API for real generation.';

export class StandaloneGenerationGateway implements GenerationGateway {
  async generate(
    request: GenerationRequest,
    _identity: CreatorIdentity | null
  ): Promise<GenerationResult> {
    const provenance = {
      source: 'ai' as const,
      prompt: request.prompt,
      provider: 'standalone-stub',
      costCredits: 0,
      generatedAt: new Date().toISOString(),
    };

    // For scenes, return a single placeholder object so the pipe is testable
    // end-to-end before the real generator exists.
    if (request.target === 'scene') {
      const placeholder: SceneObject = {
        uuid: 'stub-' + Math.random().toString(36).slice(2, 9),
        name: 'Generated Placeholder',
        type: 'cube',
        position: { x: 0, y: 0.5, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        color: '#7C3AED',
      };
      return {
        target: request.target,
        artifact: [placeholder] as SceneObject[],
        provenance,
        warnings: [STUB_WARNING],
      };
    }

    return {
      target: request.target,
      artifact: { note: STUB_WARNING, echo: request.prompt },
      provenance,
      warnings: [STUB_WARNING],
    };
  }
}

export class StandaloneAssetStore implements AssetStore {
  private store = new Map<string, StoredAsset>();

  async put(input: {
    name: string;
    kind: string;
    data: string;
    contentType?: string;
  }): Promise<StoredAsset> {
    const id = 'asset-' + Math.random().toString(36).slice(2, 10);
    const contentType = input.contentType ?? 'application/octet-stream';
    const asset: StoredAsset = {
      id,
      url: `data:${contentType};base64,${input.data}`,
      kind: input.kind,
      name: input.name,
      contentType,
    };
    this.store.set(id, asset);
    return asset;
  }
  async get(id: string): Promise<StoredAsset | null> {
    return this.store.get(id) ?? null;
  }
  async list(): Promise<StoredAsset[]> {
    return Array.from(this.store.values());
  }
}

export class StandalonePublishTarget implements PublishTarget {
  async publish(
    game: Game,
    _identity: CreatorIdentity | null
  ): Promise<PublishResult> {
    // Standalone "publish" = export the game as a downloadable JSON blob.
    const json = JSON.stringify(game, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    return {
      status: 'exported',
      url,
      details: `${game.title || 'game'}.json`,
    };
  }
}

// ============================================================
// Registry — the single config-swap point
// ============================================================
// Today everything is standalone. When the integrated adapters land, branch on
// VITE_INTEGRATION_MODE here to return them instead. Nothing else changes.

const MODE: string =
  (import.meta as any)?.env?.VITE_INTEGRATION_MODE ?? 'standalone';

export const integration = {
  mode: MODE,
  identity: new StandaloneIdentityProvider() as IdentityProvider,
  generation: new StandaloneGenerationGateway() as GenerationGateway,
  assets: new StandaloneAssetStore() as AssetStore,
  publish: new StandalonePublishTarget() as PublishTarget,
};
